import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Leaf, ArrowLeft, EnvelopeSimple, ShieldCheck, Eye, EyeSlash } from '@phosphor-icons/react';

export default function ForgotPasswordPage() {
    const [step, setStep] = useState('email'); // 'email' | 'code'
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const { api } = useAuth();

    const handleSendCode = async (e) => {
        e.preventDefault();
        if (!email) { toast.error('Please enter your email'); return; }
        setLoading(true);
        try {
            await api().post('/auth/forgot-password', { email });
            toast.success('Reset code sent! Check your email.');
            setStep('code');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to send reset code');
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (!code || code.length !== 6) { toast.error('Please enter the 6-digit code'); return; }
        if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
        if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
        setLoading(true);
        try {
            await api().post('/auth/reset-password-with-code', { email, code, new_password: newPassword });
            toast.success('Password reset! You can now log in.');
            setSuccess(true);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to reset password');
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="min-h-screen bg-[hsl(53,92%,96%)] dot-grid flex items-center justify-center p-4">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
                    <div className="brutalist-card p-6 md:p-8 text-center">
                        <div className="w-16 h-16 mx-auto rounded-full bg-[#22c55e] border-4 border-[#1e293b] flex items-center justify-center mb-4">
                            <ShieldCheck weight="fill" className="text-white" size={32} />
                        </div>
                        <h1 className="font-fredoka text-2xl font-bold text-[#1e293b] mb-2">Password Reset!</h1>
                        <p className="text-[#475569] mb-6">Your password has been updated. You can now log in with your new password.</p>
                        <Link to="/login" className="brutalist-btn bg-[#22c55e] text-white inline-block" data-testid="back-to-login-btn">
                            Back to Login
                        </Link>
                    </div>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[hsl(53,92%,96%)] dot-grid flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
                <Link to="/login" className="inline-flex items-center gap-2 text-[#475569] hover:text-[#22c55e] mb-6 font-semibold" data-testid="back-link">
                    <ArrowLeft weight="bold" size={20} /> Back to Login
                </Link>

                <div className="brutalist-card p-6 md:p-8">
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 mx-auto rounded-full bg-[#f59e0b] border-4 border-[#1e293b] flex items-center justify-center mb-4">
                            {step === 'email' ? (
                                <EnvelopeSimple weight="fill" className="text-white" size={32} />
                            ) : (
                                <ShieldCheck weight="fill" className="text-white" size={32} />
                            )}
                        </div>
                        <h1 className="font-fredoka text-2xl md:text-3xl font-bold text-[#1e293b]">
                            {step === 'email' ? 'Forgot Password?' : 'Enter Reset Code'}
                        </h1>
                        <p className="text-[#475569] mt-2">
                            {step === 'email'
                                ? "Enter your email and we'll send you a reset code."
                                : `We sent a 6-digit code to ${email}`}
                        </p>
                    </div>

                    {step === 'email' ? (
                        <form onSubmit={handleSendCode} className="space-y-5">
                            <div>
                                <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">Email Address</label>
                                <input
                                    type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                                    className="brutalist-input w-full" placeholder="parent@email.com"
                                    data-testid="forgot-email-input"
                                />
                            </div>
                            <motion.button type="submit" disabled={loading} whileHover={{ y: -2, x: -2 }} whileTap={{ y: 2, x: 2 }}
                                className="brutalist-btn w-full bg-[#f59e0b] text-white text-lg disabled:opacity-50" data-testid="send-code-btn">
                                {loading ? 'Sending...' : 'Send Reset Code'}
                            </motion.button>
                        </form>
                    ) : (
                        <form onSubmit={handleResetPassword} className="space-y-5">
                            <div>
                                <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">6-Digit Code</label>
                                <input
                                    type="text" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    className="brutalist-input w-full text-center text-2xl tracking-[0.5em] font-mono"
                                    placeholder="000000" maxLength={6}
                                    data-testid="reset-code-input"
                                />
                            </div>
                            <div>
                                <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">New Password</label>
                                <div className="relative">
                                    <input
                                        type={showPassword ? 'text' : 'password'} value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        className="brutalist-input w-full pr-12" placeholder="At least 6 characters"
                                        data-testid="new-password-input"
                                    />
                                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-[#475569]">
                                        {showPassword ? <EyeSlash size={24} /> : <Eye size={24} />}
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className="block font-fredoka font-semibold text-[#1e293b] mb-2">Confirm Password</label>
                                <input
                                    type="password" value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="brutalist-input w-full" placeholder="Re-enter password"
                                    data-testid="confirm-password-input"
                                />
                            </div>
                            <motion.button type="submit" disabled={loading} whileHover={{ y: -2, x: -2 }} whileTap={{ y: 2, x: 2 }}
                                className="brutalist-btn w-full bg-[#22c55e] text-white text-lg disabled:opacity-50" data-testid="reset-password-btn">
                                {loading ? 'Resetting...' : 'Reset Password'}
                            </motion.button>
                            <button type="button" onClick={() => { setStep('email'); setCode(''); }}
                                className="w-full text-center text-sm text-[#475569] hover:text-[#22c55e] font-semibold"
                                data-testid="resend-link">
                                Didn't get a code? Send again
                            </button>
                        </form>
                    )}
                </div>
            </motion.div>
        </div>
    );
}
